import { useEffect, useMemo, useState } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer,
} from 'recharts';
import { useCopilotAdoption } from '@/lib/queries';
import { useSession } from '@/store/session';
import { useResults } from '@/store/results';
import { CAPABILITY_SCOPES } from '@/auth/scopes';
import { computeRoi } from './adoption';
import { Panel } from '@/components/Panel';
import { ModuleGate } from '@/components/ModuleGate';
import { Metric, MetricGrid } from '@/components/Metric';
import { ErrorNotice } from '@/components/ErrorNotice';
import { IconTrend } from '@/components/icons';
import { readToken } from '@/lib/theme';

const pct = (n: number) => `${Math.round(n * 100)}%`;
const money = (n: number) =>
  new Intl.NumberFormat(undefined, { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n);

/**
 * C3 — Copilot adoption & seat ROI (Reports.Read.All). Read happens only after the
 * admin grants this module (§4). The per-seat cost input is client-side only and
 * never leaves the browser, so ROI stays inside the zero-egress moat.
 */
export function AdoptionPanel() {
  const consented = useSession((s) => s.hasConsent('C3'));
  const { data, isLoading, isError, error } = useCopilotAdoption(consented);
  const setAdoption = useResults((s) => s.setAdoption);
  const [costInput, setCostInput] = useState('30');

  useEffect(() => {
    setAdoption(data ?? null);
  }, [data, setAdoption]);

  const accent = readToken('--accent');
  const grid = readToken('--border', '#D4DDE2');
  const roi = useMemo(
    () => (data ? computeRoi(data, Number(costInput)) : null),
    [data, costInput],
  );

  return (
    <Panel id="c3" icon={<IconTrend />} title="Copilot adoption & seat ROI" cap="C3"
      why={CAPABILITY_SCOPES.C3.why}>
      <ModuleGate cap="C3">
        {isLoading && <p className="panel__loading">Reading usage reports…</p>}
        {isError && <ErrorNotice error={error} />}

        {data && (
          <>
            <MetricGrid>
              <Metric label="Enabled seats" value={data.enabledSeats} sub="Provisioned with Copilot" />
              <Metric label="Active (period)" value={data.activeSeats} tone="green"
                sub={data.reportRefreshDate ? `As of ${data.reportRefreshDate}` : 'Any Copilot activity'} />
              <Metric label="Adoption rate" value={pct(data.adoptionRate)} tone="accent"
                sub="Active ÷ enabled" />
              <Metric label="Idle seats" value={data.idleSeats} tone={data.idleSeats > 0 ? 'red' : 'green'}
                sub="Paid but unused" />
            </MetricGrid>

            {data.enabledSeats === 0 ? (
              <div className="notice notice--info">
                <strong>No Copilot usage rows returned</strong>
                <p>The tenant may have no Copilot seats provisioned yet, or the beta usage report
                  is not enabled here. This module is best-effort by design.</p>
              </div>
            ) : (
              <>
                <h3 className="metric__label" style={{ marginTop: 8 }}>Active users by app</h3>
                <div className="chart">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={data.perApp} margin={{ top: 8, right: 8, bottom: 4, left: -18 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke={grid} vertical={false} />
                      <XAxis dataKey="label" tick={{ fontSize: 12, fill: readToken('--text-3', '#6E7478') }}
                        tickLine={false} axisLine={{ stroke: grid }} />
                      <YAxis allowDecimals={false} tick={{ fontSize: 12, fill: readToken('--text-3', '#6E7478') }}
                        tickLine={false} axisLine={false} width={44} />
                      <Tooltip cursor={{ fill: readToken('--bg-2', '#EFF3F6') }}
                        contentStyle={{
                          borderRadius: 10, border: `1px solid ${grid}`,
                          fontFamily: 'var(--font-mono)', fontSize: 12,
                        }} />
                      <Bar dataKey="activeUsers" name="Active users" fill={accent} radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>

                <h3 className="metric__label" style={{ marginTop: 14 }}>Seat ROI</h3>
                <div className="field">
                  <label htmlFor="cost">Cost per seat / month (USD, local only)</label>
                  <input id="cost" inputMode="decimal" value={costInput}
                    onChange={(e) => setCostInput(e.target.value.replace(/[^0-9.]/g, ''))} />
                </div>
                {roi && (
                  <MetricGrid>
                    <Metric label="Monthly spend" value={money(roi.monthlySpend)} sub="Enabled seats × cost" />
                    <Metric label="Wasted / month" value={money(roi.wastedMonthlySpend)}
                      tone={roi.wastedMonthlySpend > 0 ? 'red' : 'green'} sub="Idle seats × cost" />
                    <Metric label="Annualised waste" value={money(roi.annualWaste)}
                      tone={roi.annualWaste > 0 ? 'red' : 'green'} sub="Idle seats × cost × 12" />
                  </MetricGrid>
                )}
              </>
            )}
          </>
        )}
      </ModuleGate>
    </Panel>
  );
}
